(() => {
  if (customElements.get('brkox-frame-product')) return;
  class FrameProduct extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      const on = (el, event, fn) => el?.addEventListener(event, fn, {signal});
      const q = selector => this.querySelector(selector);
      const qa = selector => [...this.querySelectorAll(selector)];
      const form = q('form'), native = q('[data-frame-variant]');
      const data = q('[data-frame-data]');
      if (!form || !native || !data) return;
      let variants;
      try { variants = JSON.parse(data.textContent); } catch (_) { return; }
      const strings = JSON.parse(q('[data-frame-strings]').textContent);
      const add = q('[data-frame-add]'), addText = q('[data-frame-add-text]');
      const sticky = q('[data-frame-sticky-add]'), qty = q('[data-frame-quantity]');
      const error = q('[data-frame-error]'), dialog = q('dialog');
      const radios = qa('[data-frame-option]');
      let current = variants.find(v => String(v.id) === native.value) || variants[0];
      if (!current || !add || !qty) return;
      let selected = [...current.options], busy = false;
      const exact = values => variants.find(v => v.options.every((value, i) => value === values[i]));
      const normalizeQuantity = () => {
        const min = Number(qty.min) || 1, step = Number(qty.step) || 1;
        const max = qty.max ? Number(qty.max) : Infinity;
        const value = Number.isFinite(qty.valueAsNumber) ? qty.valueAsNumber : min;
        qty.value = Math.max(min, Math.min(max, min + Math.max(0, Math.round((value-min)/step))*step));
      };
      const panes = qa('[data-frame-media]');
      const thumbnails = qa('[data-frame-thumb]');
      const images = qa('[data-frame-zoom]');
      const stage = q('[data-frame-gallery]');
      const strip = q('[data-frame-thumbnail-strip]');
      const mobile = window.matchMedia('(max-width: 749px)');
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      let activeId = current.media_id || panes[0]?.dataset.frameMedia;
      let scrollFrame = 0, drag, ignoreClickUntil = 0, previousOverflow;
      const imageCount = index => strings.count.replace('__CURRENT__', index + 1).replace('__TOTAL__', images.length);
      const updateDialog = () => {
        const index = images.findIndex(link => link.closest('[data-frame-media]').dataset.frameMedia === activeId);
        if (index < 0 || !dialog) return;
        const image = dialog.querySelector('img');
        image.src = images[index].href;
        image.alt = images[index].querySelector('img')?.alt || '';
        q('[data-frame-dialog-count]').textContent = imageCount(index);
      };
      const updateThumbArrows = () => {
        if (!strip) return;
        q('[data-frame-thumbs="-1"]').disabled = strip.scrollLeft < 2;
        q('[data-frame-thumbs="1"]').disabled = strip.scrollLeft + strip.clientWidth >= strip.scrollWidth - 2;
      };
      const showMedia = (id, align = true) => {
        const pane = panes.find(el => el.dataset.frameMedia === String(id));
        if (!pane) return;
        activeId = String(id);
        panes.forEach(el => {
          el.hidden = !mobile.matches && el !== pane;
          if (el !== pane) {
            el.querySelectorAll('video').forEach(video => video.pause());
            el.querySelectorAll('iframe').forEach(frame => {
              if (!frame.contentWindow) return;
              const origin = new URL(frame.src, window.location.href).origin;
              frame.contentWindow.postMessage(JSON.stringify({event:'command',func:'pauseVideo',args:[]}), origin);
              frame.contentWindow.postMessage(JSON.stringify({method:'pause'}), origin);
            });
          }
        });
        thumbnails.forEach(el => {
          if (el.dataset.frameThumb === String(id)) el.setAttribute('aria-current','true');
          else el.removeAttribute('aria-current');
        });
        const thumb = thumbnails.find(el => el.dataset.frameThumb === activeId);
        if (thumb && strip?.clientWidth) {
          const left = thumb.getBoundingClientRect().left - strip.getBoundingClientRect().left + strip.scrollLeft;
          if (left < strip.scrollLeft || left + thumb.offsetWidth > strip.scrollLeft + strip.clientWidth) {
            strip.scrollTo({left: Math.max(0, left - (strip.clientWidth - thumb.offsetWidth) / 2), behavior: reducedMotion.matches || dialog?.open ? 'instant' : 'smooth'});
          }
        }
        const count = q('[data-frame-count]');
        if (count) count.textContent = `${panes.indexOf(pane) + 1} / ${panes.length}`;
        if (mobile.matches && align && stage?.clientWidth) {
          stage.scrollTo({left: panes.indexOf(pane) * stage.clientWidth, behavior: 'instant'});
        }
        if (dialog?.open) updateDialog();
      };
      const alignGallery = () => {
        showMedia(activeId);
        updateThumbArrows();
      };
      on(mobile, 'change', alignGallery);
      if ('ResizeObserver' in window && stage) {
        this.galleryObserver = new ResizeObserver(alignGallery);
        this.galleryObserver.observe(stage);
      }
      on(stage, 'scroll', () => {
        if (scrollFrame || !mobile.matches) return;
        scrollFrame = requestAnimationFrame(() => {
          scrollFrame = 0;
          if (!stage.clientWidth || dialog?.open) return;
          const index = Math.max(0, Math.min(panes.length - 1, Math.round(stage.scrollLeft / stage.clientWidth)));
          if (panes[index]?.dataset.frameMedia !== activeId) showMedia(panes[index].dataset.frameMedia, false);
        });
      });
      on(strip, 'scroll', updateThumbArrows);
      qa('[data-frame-thumbs]').forEach(button => on(button, 'click', () => {
        strip.scrollBy({left: Number(button.dataset.frameThumbs) * strip.clientWidth, behavior: reducedMotion.matches ? 'instant' : 'smooth'});
      }));
      on(stage, 'dragstart', event => event.preventDefault());
      on(stage, 'pointerdown', event => {
        if (!mobile.matches || event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest('video, iframe, model-viewer')) return;
        drag = {x: event.clientX, y: event.clientY, left: stage.scrollLeft, id: event.pointerId, horizontal: false};
      });
      on(stage, 'pointermove', event => {
        if (!drag || drag.id !== event.pointerId) return;
        const dx = event.clientX - drag.x, dy = event.clientY - drag.y;
        if (!drag.horizontal && Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy) * 1.25) {
          drag.horizontal = true;
          stage.classList.add('is-dragging');
          stage.setPointerCapture(event.pointerId);
        }
        if (!drag.horizontal) return;
        event.preventDefault();
        stage.scrollLeft = drag.left - dx;
      });
      const finishDrag = event => {
        if (!drag || drag.id !== event.pointerId) return;
        const horizontal = drag.horizontal;
        drag = undefined;
        stage.classList.remove('is-dragging');
        if (stage.hasPointerCapture(event.pointerId)) stage.releasePointerCapture(event.pointerId);
        if (!horizontal) return;
        ignoreClickUntil = Date.now() + 500;
        const index = Math.max(0, Math.min(panes.length - 1, Math.round(stage.scrollLeft / stage.clientWidth)));
        showMedia(panes[index].dataset.frameMedia);
      };
      on(stage, 'pointerup', finishDrag);
      on(stage, 'pointercancel', finishDrag);
      const moveImage = direction => {
        if (images.length < 2) return;
        const index = Math.max(0, images.findIndex(link => link.closest('[data-frame-media]').dataset.frameMedia === activeId));
        const next = images[(index + direction + images.length) % images.length];
        showMedia(next.closest('[data-frame-media]').dataset.frameMedia);
      };
      on(q('[data-frame-prev]'), 'click', () => moveImage(-1));
      on(q('[data-frame-next]'), 'click', () => moveImage(1));
      on(dialog, 'keydown', event => {
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        moveImage(event.key === 'ArrowLeft' ? -1 : 1);
      });
      const swipeSurface = q('[data-frame-dialog-swipe]');
      let swipeStart;
      on(swipeSurface, 'dragstart', event => event.preventDefault());
      on(swipeSurface, 'pointerdown', event => {
        if (!event.isPrimary || event.button !== 0 || event.target.closest('button')) return;
        swipeStart = {x: event.clientX, y: event.clientY, id: event.pointerId};
        swipeSurface.setPointerCapture(event.pointerId);
      });
      on(swipeSurface, 'pointercancel', () => {swipeStart = undefined;});
      on(swipeSurface, 'pointerup', event => {
        if (!swipeStart || swipeStart.id !== event.pointerId) return;
        const dx = event.clientX - swipeStart.x, dy = event.clientY - swipeStart.y;
        swipeStart = undefined;
        if (Math.abs(dx) > 44 && Math.abs(dx) > Math.abs(dy) * 1.25) moveImage(dx < 0 ? 1 : -1);
      });
      on(dialog, 'close', () => {
        if (previousOverflow !== undefined) document.documentElement.style.overflow = previousOverflow;
        previousOverflow = undefined;
      });
      this.restoreGallery = () => {
        cancelAnimationFrame(scrollFrame);
        if (dialog?.open) dialog.close();
        if (previousOverflow !== undefined) document.documentElement.style.overflow = previousOverflow;
      };
      const buttonState = () => {
        const available = !!current?.available;
        const text = busy ? strings.adding : !current ? strings.unavailable : available ? strings.add : strings.soldOut;
        add.disabled = busy || !available;
        addText.textContent = text;
        if (sticky) {sticky.disabled = add.disabled; sticky.textContent = text;}
        form.setAttribute('aria-busy',String(busy));
        qa('[data-frame-quantity-step]').forEach(el => el.disabled = busy);
        qty.disabled = busy;
        radios.forEach(input => {
          const values = [...selected];
          values[Number(input.dataset.frameOption)] = input.value;
          input.disabled = busy || !exact(values);
        });
        const accelerated = q('.brkox-frame-v2__accelerated');
        if (accelerated) accelerated.hidden = !available;
      };
      const update = (writeUrl = false, changeImage = true) => {
        current = exact(selected);
        native.value = current ? String(current.id) : '';
        q('[data-frame-price]').textContent = current?.money || strings.unavailable;
        const compare = q('[data-frame-compare]');
        compare.textContent = current?.compare_money || '';
        compare.hidden = !current?.on_sale;
        q('[data-frame-sticky-price]').textContent = current?.money || strings.unavailable;
        const size = q('[data-frame-size]'), sizeRow = q('[data-frame-size-row]');
        if (size) {size.textContent = current?.size || '';sizeRow.hidden = !current?.size;}
        const detailSize = q('[data-frame-detail-size]');
        if (detailSize) detailSize.textContent = current?.size || '';
        qty.min = current?.min || 1; qty.step = current?.step || 1; qty.max = current?.max || '';
        normalizeQuantity();
        radios.forEach(input => {
          const i = Number(input.dataset.frameOption);
          input.checked = input.value === selected[i];
          const values = [...selected]; values[i] = input.value;
          const candidate = exact(values);
          input.disabled = !candidate;
          const wrap = input.closest('.brkox-frame__value');
          const price = wrap.querySelector('[data-option-price]');
          if (price) price.textContent = candidate?.money || strings.unavailable;
          const stock = wrap.querySelector('[data-option-stock]');
          if (stock) {stock.hidden = !candidate || candidate.available;stock.textContent = strings.soldOut;}
        });
        buttonState();
        if (changeImage && current?.media_id) showMedia(current.media_id);
        error.hidden = true;
        if (writeUrl) {
          const url = new URL(window.location.href);
          if (current) url.searchParams.set('variant', current.id); else url.searchParams.delete('variant');
          history.replaceState({},'',url);
          form.dispatchEvent(new CustomEvent('variant:change',{bubbles:true,detail:{variant:current?.raw || null}}));
        }
      };
      radios.forEach(input => on(input,'change',() => {selected[Number(input.dataset.frameOption)] = input.value;update(true);}));
      on(native,'change',() => {const v = variants.find(item => String(item.id) === native.value);if(v){selected=[...v.options];update(true);}});
      qa('[data-frame-quantity-step]').forEach(btn => on(btn,'click',() => {
        normalizeQuantity(); qty.value = Number(qty.value) + Number(btn.dataset.frameQuantityStep)*(Number(qty.step)||1); normalizeQuantity();
        qty.dispatchEvent(new Event('change',{bubbles:true}));
      }));
      on(qty,'change',normalizeQuantity);
      qa('[data-frame-thumb]').forEach(thumb => on(thumb,'click',event => {event.preventDefault();showMedia(thumb.dataset.frameThumb);}));
      qa('[data-frame-zoom]').forEach(link => on(link,'click',event => {
        if (!dialog || typeof dialog.showModal !== 'function') return;
        event.preventDefault();
        if (event.detail !== 0 && Date.now() < ignoreClickUntil) return;
        showMedia(link.closest('[data-frame-media]').dataset.frameMedia);
        updateDialog();
        if (dialog.open) return;
        previousOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
        dialog.showModal();
      }));
      qa('[data-frame-prev], [data-frame-next]').forEach(button => button.hidden = images.length < 2);
      const details = qa('.brkox-frame-simple__item');
      details.forEach(item => on(item, 'toggle', () => {
        if (item.open) details.forEach(other => {if (other !== item) other.open = false;});
      }));
      on(q('[data-frame-close]'),'click',() => dialog?.close());
      on(dialog,'click',event => {if(event.target===dialog)dialog.close();});
      on(sticky,'click',() => {if(!add.disabled)form.requestSubmit(add);});
      if ('IntersectionObserver' in window && sticky) {
        this.observer = new IntersectionObserver(entries => {
          const entry=entries[0];this.toggleAttribute('data-sticky-active',!entry.isIntersecting && entry.boundingClientRect.top<0);
        });
        this.observer.observe(add);
      }
      on(form,'submit',async event => {
        if(busy || !current?.available){event.preventDefault();return;}
        normalizeQuantity();
        const cart=document.querySelector('cart-drawer');
        if(!cart || typeof cart.renderContents!=='function' || typeof cart.getSectionsToRender!=='function')return;
        event.preventDefault();
        const body=new FormData(form);
        for(const key of [...body.keys()])if(key.startsWith('frame-option-'))body.delete(key);
        const route=window.Shopify?.routes?.root || '/';
        busy=true;buttonState();error.hidden=true;
        let added=false;
        try{
          body.set('sections',cart.getSectionsToRender().map(section=>section.id).join(','));
          body.set('sections_url',window.location.pathname);
          cart.setActiveElement?.(document.activeElement);
          const response=await fetch(route+'cart/add.js',{method:'POST',headers:{Accept:'application/json','X-Requested-With':'XMLHttpRequest'},body});
          const result=await response.json();
          if(!response.ok || result.status)throw new Error(result.description || strings.error);
          added=true;cart.classList.remove('is-empty');cart.renderContents(result,true);
          document.dispatchEvent(new CustomEvent('cart:updated',{bubbles:true,detail:result}));
        }catch(failure){
          if(added){window.location.assign(route+'cart');return;}
          error.textContent=failure instanceof TypeError ? strings.uncertain : failure.message;
          error.hidden=false;
        }finally{busy=false;buttonState();}
      });
      this.dataset.enhanced='true';update(false,false);alignGallery();
    }
    disconnectedCallback(){this.restoreGallery?.();this.controller?.abort();this.controller=null;this.observer?.disconnect();this.galleryObserver?.disconnect();delete this.dataset.enhanced;}
  }
  customElements.define('brkox-frame-product',FrameProduct);
})();
